import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check session status on mount
    checkSession();
    // Set up interval to check session every minute
    const interval = setInterval(checkSession, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkSession = async () => {
    try {
      const response = await fetch('/includes/auth/check_session.php');
      const data = await response.json();
      
      setIsAuthenticated(data.authenticated);
      if (data.expired) {
        // Handle session expiration
        window.location.href = '/resume';
      }
    } catch (error) {
      console.error('Session check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (pin) => {
    try {
      const response = await fetch('/includes/auth/verify_pin.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pin })
      });

      const data = await response.json();
      
      if (data.success) {
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { 
          success: false, 
          message: data.message || 'Invalid PIN'
        };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        message: 'An error occurred. Please try again later.'
      };
    }
  };

  const logout = async () => {
    try {
      await fetch('/includes/auth/logout.php');
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        login, 
        logout,
        loading 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;